// Onboarding wizard — /onboarding.
//
// Replaces web/onboarding.html + web/scripts/onboarding.js. State
// machine with 5 phases:
//   1. pick   — datasource tile picker
//   2. context — org description + fiscal year
//   3. tables — schema accordion + table selection
//   4. build  — kicks off /start; polls /progress every 3s
//   5. review — QA pairs review (read-only in this initial port;
//               deep-edit forms land in a follow-up commit on this
//               branch)
//   6. done   — final confirmation panel
//
// State is persisted in sessionStorage so a refresh mid-wizard
// resumes where the user left off (matches the legacy onboarding).

import { useCallback, useEffect, useState } from "react";
import { onboardingApi } from "../../api/onboarding";
import type {
  DatasourceTile,
  ProgressPhase,
  ProgressResponse,
  QAPair,
  SchemaGroup,
  UserContext,
} from "../../api/onboarding";
import { ErrorBlock, LoadingBlock } from "../admin/_shared";

type Phase = "pick" | "context" | "tables" | "build" | "review" | "done";

const SS_KEYS = {
  jobId: "onb_job_id",
  phase: "onb_step",
  dsId: "onb_ds_id",
  dsName: "onb_ds_name",
  includeSchemas: "onb_include_schemas",
};

interface Props {
  bearer: string;
}

export function OnboardingPage({ bearer }: Props) {
  const isAddMode = new URLSearchParams(window.location.search).get("mode") === "add";

  const [phase, setPhase] = useState<Phase>(() => {
    const saved = sessionStorage.getItem(SS_KEYS.phase);
    if (saved === "pick" || saved === "context" || saved === "tables" || saved === "build" || saved === "review" || saved === "done") {
      return saved;
    }
    return "pick";
  });

  const [datasources, setDatasources] = useState<DatasourceTile[]>([]);
  const [dsLoadError, setDsLoadError] = useState<Error | null>(null);

  const [selectedDs, setSelectedDs] = useState<DatasourceTile | null>(null);
  const [userContext, setUserContext] = useState<UserContext>({
    description: "",
    fiscal_year_start_month: 1,
    detect_choice_fields: true,
  });
  const [schemas, setSchemas] = useState<SchemaGroup[]>([]);
  const [selectedTables, setSelectedTables] = useState<Set<string>>(new Set());

  const [jobId, setJobId] = useState<string | null>(
    () => sessionStorage.getItem(SS_KEYS.jobId),
  );
  const [progress, setProgress] = useState<ProgressPhase | null>(null);
  const [buildError, setBuildError] = useState<Error | null>(null);
  const [qaPairs, setQaPairs] = useState<QAPair[]>([]);

  const persistPhase = (p: Phase) => {
    sessionStorage.setItem(SS_KEYS.phase, p);
    setPhase(p);
  };

  const resetSession = useCallback(() => {
    sessionStorage.removeItem(SS_KEYS.jobId);
    sessionStorage.removeItem(SS_KEYS.phase);
    sessionStorage.removeItem(SS_KEYS.dsId);
    sessionStorage.removeItem(SS_KEYS.dsName);
    sessionStorage.removeItem(SS_KEYS.includeSchemas);
    setJobId(null);
    setSelectedDs(null);
    setSchemas([]);
    setSelectedTables(new Set());
    setQaPairs([]);
    setProgress(null);
    setBuildError(null);
    persistPhase("pick");
  }, []);

  // --- Step 1: load datasource tiles --------------------------------------
  useEffect(() => {
    let cancelled = false;
    onboardingApi
      .listDatasources(bearer)
      .then((d) => {
        if (cancelled) return;
        setDatasources(d.datasources || []);
        // Restore selected ds if we're mid-flow.
        const savedId = sessionStorage.getItem(SS_KEYS.dsId);
        if (savedId) {
          const found = (d.datasources || []).find((x) => String(x.id) === savedId) || null;
          setSelectedDs(found);
        }
      })
      .catch((e: Error) => {
        if (!cancelled) setDsLoadError(e);
      });
    return () => {
      cancelled = true;
    };
  }, [bearer]);

  // --- Build phase: poll /progress ---------------------------------------
  useEffect(() => {
    if (phase !== "build" || !jobId) return;
    let cancelled = false;
    let failures = 0;
    const tick = async () => {
      try {
        const data = await onboardingApi.progress(bearer, jobId);
        if (cancelled) return;
        failures = 0;
        applyProgressUpdate(data);
      } catch (e) {
        if (cancelled) return;
        failures++;
        if (failures >= 10) {
          setBuildError(new Error("Lost connection to server. Please refresh and try again."));
          return;
        }
      }
      if (!cancelled) setTimeout(tick, 3000);
    };
    void tick();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, jobId, bearer]);

  const applyProgressUpdate = (data: ProgressResponse) => {
    if (data.status === "done") {
      setQaPairs(data.qa_pairs || []);
      setProgress(null);
      setTimeout(() => persistPhase("review"), 400);
      return;
    }
    if (data.status === "failed") {
      setBuildError(new Error(data.error || "Generation failed. Please try again."));
      return;
    }
    if (data.progress) setProgress(data.progress);
  };

  // --- Step transitions ---------------------------------------------------
  const onPickDatasource = (ds: DatasourceTile) => {
    setSelectedDs(ds);
    sessionStorage.setItem(SS_KEYS.dsId, String(ds.id));
    sessionStorage.setItem(SS_KEYS.dsName, ds.name);
    if (ds.include_schemas) {
      sessionStorage.setItem(SS_KEYS.includeSchemas, JSON.stringify(ds.include_schemas));
    } else {
      sessionStorage.removeItem(SS_KEYS.includeSchemas);
    }
    if (isAddMode) {
      // Org context already set — skip the context panel.
      void loadTables(ds);
    } else {
      // Default fiscal year + seed description from any org_context the
      // backend tile provides.
      setUserContext((cur) => ({
        ...cur,
        description: ds.org_context || cur.description,
      }));
      persistPhase("context");
    }
  };

  const onContextNext = () => {
    if (!selectedDs) return;
    void loadTables(selectedDs);
  };

  const loadTables = async (ds: DatasourceTile) => {
    persistPhase("tables");
    try {
      const data = await onboardingApi.listDatasourceTables(
        bearer,
        ds.id,
        ds.include_schemas ?? undefined,
      );
      setSchemas(data.schemas || []);
      // Pre-select everything by default — matches the legacy UX.
      const init = new Set<string>();
      (data.schemas || []).forEach((s) =>
        s.tables.forEach((t) => init.add(`${s.schema_name}.${t.table_name}`)),
      );
      setSelectedTables(init);
    } catch (e) {
      setBuildError(e as Error);
    }
  };

  const onStartBuild = async () => {
    if (!selectedDs) return;
    const tables = Array.from(selectedTables).map((key) => {
      const [schema, ...rest] = key.split(".");
      return { schema_name: schema, table_name: rest.join(".") };
    });
    persistPhase("build");
    setProgress({ phase: "connecting", message: "Connecting…" });
    setBuildError(null);
    try {
      const res = await onboardingApi.start(bearer, {
        datasource_id: selectedDs.id,
        selected_tables: tables.length === 0 ? null : tables,
        user_context: userContext,
        include_schemas: selectedDs.include_schemas ?? undefined,
      });
      setJobId(res.job_id);
      sessionStorage.setItem(SS_KEYS.jobId, res.job_id);
    } catch (e) {
      setBuildError(e as Error);
    }
  };

  const onFinalize = async () => {
    if (!jobId) return;
    try {
      await onboardingApi.complete(bearer, jobId);
      persistPhase("done");
    } catch (e) {
      setBuildError(e as Error);
    }
  };

  // --- Render -------------------------------------------------------------
  return (
    <div className="max-w-3xl mx-auto px-6 pt-8 pb-12">
      <Stepper current={phase} />

      <div className="mt-8">
        {phase === "pick" && (
          <PickStep
            datasources={datasources}
            error={dsLoadError}
            onPick={onPickDatasource}
          />
        )}

        {phase === "context" && selectedDs && (
          <ContextStep
            ds={selectedDs}
            value={userContext}
            onChange={setUserContext}
            onBack={() => persistPhase("pick")}
            onNext={onContextNext}
          />
        )}

        {phase === "tables" && selectedDs && (
          <TablesStep
            ds={selectedDs}
            schemas={schemas}
            selected={selectedTables}
            onToggle={(key) =>
              setSelectedTables((cur) => {
                const next = new Set(cur);
                next.has(key) ? next.delete(key) : next.add(key);
                return next;
              })
            }
            onToggleAll={(schemaName, on) =>
              setSelectedTables((cur) => {
                const next = new Set(cur);
                const group = schemas.find((s) => s.schema_name === schemaName);
                if (!group) return next;
                for (const t of group.tables) {
                  const k = `${schemaName}.${t.table_name}`;
                  if (on) next.add(k);
                  else next.delete(k);
                }
                return next;
              })
            }
            onBack={() => persistPhase(isAddMode ? "pick" : "context")}
            onNext={onStartBuild}
          />
        )}

        {phase === "build" && (
          <BuildStep
            progress={progress}
            error={buildError}
            onRestart={resetSession}
          />
        )}

        {phase === "review" && (
          <ReviewStep qaPairs={qaPairs} onFinalize={onFinalize} />
        )}

        {phase === "done" && <DoneStep />}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stepper
// ---------------------------------------------------------------------------
function Stepper({ current }: { current: Phase }) {
  const order: Array<{ id: Phase; label: string }> = [
    { id: "pick", label: "Datasource" },
    { id: "context", label: "Context" },
    { id: "tables", label: "Tables" },
    { id: "build", label: "Build" },
    { id: "review", label: "Review" },
    { id: "done", label: "Done" },
  ];
  const idx = order.findIndex((s) => s.id === current);
  return (
    <ol className="flex items-center justify-between gap-2 text-xs">
      {order.map((s, i) => (
        <li key={s.id} className="flex-1 flex items-center gap-2 min-w-0">
          <span
            className={
              "inline-flex h-7 w-7 rounded-full items-center justify-center text-[11px] font-semibold shrink-0 " +
              (i < idx
                ? "bg-emerald-600 text-white"
                : i === idx
                  ? "bg-brand text-white"
                  : "bg-neutral-100 text-neutral-500")
            }
          >
            {i + 1}
          </span>
          <span
            className={
              "truncate " +
              (i <= idx ? "text-neutral-900 font-medium" : "text-neutral-500")
            }
          >
            {s.label}
          </span>
          {i < order.length - 1 && (
            <span className="flex-1 h-px bg-neutral-200 mx-1" aria-hidden />
          )}
        </li>
      ))}
    </ol>
  );
}

// ---------------------------------------------------------------------------
// Step 1: Datasource picker
// ---------------------------------------------------------------------------
function PickStep({
  datasources,
  error,
  onPick,
}: {
  datasources: DatasourceTile[];
  error: Error | null;
  onPick: (ds: DatasourceTile) => void;
}) {
  if (error) return <ErrorBlock error={error} />;
  if (datasources.length === 0) return <LoadingBlock />;

  return (
    <div>
      <h1 className="text-xl font-semibold text-neutral-900">Connect a datasource</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Pick the source of truth Agami should learn from. You can connect more later.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {datasources.map((ds) => {
          const disabled = !ds.available && !ds.already_added;
          const added = ds.already_added === true;
          return (
            <button
              key={ds.id}
              type="button"
              onClick={() => !disabled && !added && onPick(ds)}
              disabled={disabled || added}
              className={
                "text-left rounded-[14px] border p-4 transition-colors flex flex-col gap-2 min-h-[160px] " +
                (disabled
                  ? "border-line bg-neutral-50 cursor-not-allowed opacity-60"
                  : added
                    ? "border-emerald-200 bg-emerald-50 cursor-not-allowed"
                    : "border-line bg-white hover:border-brand hover:shadow-sm")
              }
            >
              <div className="flex items-start justify-between gap-2">
                <DsLogo logo={ds.logo} alt={ds.name} />
                {added && (
                  <span className="text-[11px] font-medium text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">
                    ✓ Added
                  </span>
                )}
                {disabled && (
                  <span className="text-[11px] text-neutral-500 bg-neutral-100 rounded-full px-2 py-0.5">
                    Coming soon
                  </span>
                )}
              </div>
              <div className="font-semibold text-neutral-900 text-sm">{ds.name}</div>
              <div className="text-xs text-neutral-600">{ds.description}</div>
              <div className="mt-auto text-[11px] uppercase tracking-wide text-neutral-500">
                {ds.db_type}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function DsLogo({ logo, alt }: { logo: string | undefined; alt: string }) {
  const map: Record<string, string> = {
    bigquery: "/images/logos/bigquery.svg",
    redshift: "/images/logos/redshift.svg",
    snowflake: "/images/logos/snowflake.svg",
    databricks: "/images/logos/databricks.svg",
    postgresql: "/images/logos/postgresql.svg",
  };
  const src = logo && map[logo] ? map[logo] : "/images/logo_h.svg";
  return (
    <img
      src={src}
      alt={alt}
      className="h-7 w-7 object-contain"
      onError={(e) => {
        (e.target as HTMLImageElement).style.display = "none";
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Step 2: Context
// ---------------------------------------------------------------------------
function ContextStep({
  ds,
  value,
  onChange,
  onBack,
  onNext,
}: {
  ds: DatasourceTile;
  value: UserContext;
  onChange: (v: UserContext) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900 mb-4"
      >
        ← Back to datasources
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">A bit about your business</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Connecting <span className="font-medium text-neutral-700">{ds.name}</span>. These
        details help Agami interpret your data.
      </p>

      <div className="mt-6 space-y-4 max-w-xl">
        <div>
          <label className="block text-sm font-medium text-neutral-800 mb-1" htmlFor="onb-description">
            What does your business do?
          </label>
          <textarea
            id="onb-description"
            value={value.description}
            onChange={(e) => onChange({ ...value, description: e.target.value })}
            rows={4}
            placeholder="E.g. We sell vintage furniture across North America. Customers buy via our website and pick up in-store…"
            className="block w-full rounded-md border border-line p-3 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-neutral-800 mb-1" htmlFor="onb-fiscal">
            Fiscal year start month
          </label>
          <select
            id="onb-fiscal"
            value={String(value.fiscal_year_start_month)}
            onChange={(e) =>
              onChange({ ...value, fiscal_year_start_month: parseInt(e.target.value, 10) })
            }
            className="block w-48 rounded-md border border-line h-9 px-2 text-sm focus:outline-none focus:ring-1 focus:ring-brand"
          >
            {[
              "January",
              "February",
              "March",
              "April",
              "May",
              "June",
              "July",
              "August",
              "September",
              "October",
              "November",
              "December",
            ].map((m, i) => (
              <option key={m} value={String(i + 1)}>
                {m}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={onNext}
          className="rounded-full bg-brand text-white text-sm font-medium h-10 px-5 hover:bg-brand-600"
        >
          Continue →
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 3: Tables
// ---------------------------------------------------------------------------
function TablesStep({
  ds,
  schemas,
  selected,
  onToggle,
  onToggleAll,
  onBack,
  onNext,
}: {
  ds: DatasourceTile;
  schemas: SchemaGroup[];
  selected: Set<string>;
  onToggle: (key: string) => void;
  onToggleAll: (schemaName: string, on: boolean) => void;
  onBack: () => void;
  onNext: () => void;
}) {
  if (schemas.length === 0) return <LoadingBlock label="Loading schema…" />;
  const totalTables = schemas.reduce((s, g) => s + g.tables.length, 0);
  const selectedCount = selected.size;
  const canContinue = selectedCount > 0;

  return (
    <div>
      <button
        type="button"
        onClick={onBack}
        className="text-xs text-neutral-500 hover:text-neutral-900 mb-4"
      >
        ← Back
      </button>
      <h1 className="text-xl font-semibold text-neutral-900">Pick the tables to include</h1>
      <p className="text-sm text-neutral-500 mt-1">
        From <span className="font-medium text-neutral-700">{ds.name}</span> · {selectedCount} of{" "}
        {totalTables} selected
      </p>

      <div className="mt-5 space-y-3">
        {schemas.map((s) => {
          const allSelected = s.tables.every((t) =>
            selected.has(`${s.schema_name}.${t.table_name}`),
          );
          const someSelected =
            !allSelected &&
            s.tables.some((t) => selected.has(`${s.schema_name}.${t.table_name}`));
          return (
            <details key={s.schema_name} className="rounded-[12px] border border-line bg-white">
              <summary className="cursor-pointer px-3 py-2 flex items-center gap-2 text-sm hover:bg-chip rounded-t-[12px]">
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={(e) => onToggleAll(s.schema_name, e.target.checked)}
                  onClick={(e) => e.stopPropagation()}
                  className="rounded border-line text-brand focus:ring-brand"
                />
                <span className="font-mono text-xs text-neutral-700">{s.schema_name}</span>
                <span className="text-xs text-neutral-500">({s.tables.length} tables)</span>
              </summary>
              <ul className="border-t border-line divide-y divide-line">
                {s.tables.map((t) => {
                  const key = `${s.schema_name}.${t.table_name}`;
                  return (
                    <li key={key} className="px-3 py-2 flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={selected.has(key)}
                        onChange={() => onToggle(key)}
                        className="rounded border-line text-brand focus:ring-brand"
                      />
                      <span className="font-mono text-xs text-neutral-700">{t.table_name}</span>
                      {t.column_count != null && (
                        <span className="ml-auto text-xs text-neutral-500 tabular-nums">
                          {t.column_count} cols
                          {t.row_count != null
                            ? ` · ${t.row_count.toLocaleString()} rows`
                            : ""}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </details>
          );
        })}
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={onNext}
          disabled={!canContinue}
          className="rounded-full bg-brand text-white text-sm font-medium h-10 px-5 hover:bg-brand-600 disabled:opacity-60 disabled:cursor-not-allowed"
        >
          Build my model →
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 4: Build (progress)
// ---------------------------------------------------------------------------
function BuildStep({
  progress,
  error,
  onRestart,
}: {
  progress: ProgressPhase | null;
  error: Error | null;
  onRestart: () => void;
}) {
  const phases: Array<{ id: string; label: string }> = [
    { id: "connecting", label: "Connecting" },
    { id: "introspecting", label: "Introspecting schema" },
    { id: "enriching", label: "Enriching with LLM" },
    { id: "validating", label: "Validating" },
    { id: "questions", label: "Generating sample questions" },
    { id: "queries", label: "Generating starter queries" },
  ];
  const activeIdx = phases.findIndex((p) => p.id === progress?.phase);

  return (
    <div>
      <h1 className="text-xl font-semibold text-neutral-900">Building your semantic model</h1>
      <p className="text-sm text-neutral-500 mt-1">
        This usually takes a couple of minutes. You can leave this page — we'll resume where
        you left off when you come back.
      </p>

      <ol className="mt-6 space-y-2">
        {phases.map((p, i) => {
          const state =
            error
              ? i < activeIdx
                ? "done"
                : i === activeIdx
                  ? "failed"
                  : "pending"
              : i < activeIdx
                ? "done"
                : i === activeIdx
                  ? "running"
                  : "pending";
          const icon =
            state === "done" ? "✓" : state === "running" ? "⟳" : state === "failed" ? "✗" : "○";
          const cls =
            state === "done"
              ? "text-emerald-700"
              : state === "running"
                ? "text-brand"
                : state === "failed"
                  ? "text-red-700"
                  : "text-neutral-400";
          const label =
            i === activeIdx && progress?.message
              ? progress.total
                ? `${progress.message} (${progress.current}/${progress.total})`
                : progress.message
              : p.label;
          return (
            <li key={p.id} className="flex items-center gap-3 text-sm">
              <span
                className={`inline-flex h-6 w-6 items-center justify-center rounded-full font-semibold ${cls} ${
                  state === "running" ? "animate-spin" : ""
                }`}
              >
                {icon}
              </span>
              <span className={cls === "text-neutral-400" ? cls : "text-neutral-800"}>
                {label}
              </span>
            </li>
          );
        })}
      </ol>

      {error && (
        <div className="mt-6 rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          <div className="font-medium">Generation failed.</div>
          <div className="mt-0.5">{error.message}</div>
          <button
            type="button"
            onClick={onRestart}
            className="mt-3 rounded-md bg-red-600 text-white px-3 h-8 text-xs hover:bg-red-700"
          >
            Start over
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 5: Review (QA pairs) — read-only in this initial port
// ---------------------------------------------------------------------------
function ReviewStep({
  qaPairs,
  onFinalize,
}: {
  qaPairs: QAPair[];
  onFinalize: () => void;
}) {
  return (
    <div>
      <h1 className="text-xl font-semibold text-neutral-900">Sample questions Agami learned</h1>
      <p className="text-sm text-neutral-500 mt-1">
        Review these to see what kind of questions Agami can already answer. Deeper editing of
        metrics, entities, and relationships is coming to the v2 wizard in a follow-up; for now
        these are the questions seeded into your library.
      </p>

      <div className="mt-6 space-y-3">
        {qaPairs.length === 0 ? (
          <div className="rounded-[14px] border border-line bg-white p-6 text-sm text-neutral-500 text-center">
            No sample questions yet.
          </div>
        ) : (
          qaPairs.map((qa, i) => (
            <div key={i} className="rounded-[14px] border border-line bg-white p-4">
              <div className="text-sm font-semibold text-neutral-900">{qa.question}</div>
              {qa.description && (
                <div className="text-xs text-neutral-600 mt-1">{qa.description}</div>
              )}
              {qa.sql && (
                <details className="mt-2">
                  <summary className="cursor-pointer text-xs text-brand hover:underline">
                    Show SQL
                  </summary>
                  <pre className="mt-2 text-xs whitespace-pre-wrap bg-neutral-50 rounded-md p-2 text-neutral-800">
                    {qa.sql}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>

      <div className="mt-8 flex justify-end">
        <button
          type="button"
          onClick={onFinalize}
          className="rounded-full bg-emerald-600 text-white text-sm font-medium h-10 px-5 hover:bg-emerald-700"
        >
          Finish setup
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Step 6: Done
// ---------------------------------------------------------------------------
function DoneStep() {
  return (
    <div className="text-center py-12">
      <div className="inline-flex h-14 w-14 rounded-full bg-emerald-100 text-emerald-700 items-center justify-center text-3xl font-semibold">
        ✓
      </div>
      <h1 className="mt-4 text-xl font-semibold text-neutral-900">All set</h1>
      <p className="mt-2 text-sm text-neutral-600 max-w-sm mx-auto">
        Your semantic model is published and Agami is ready to answer questions about your
        data.
      </p>
      <a
        href="/"
        className="mt-6 inline-block rounded-full bg-brand text-white text-sm font-medium h-10 px-5 leading-10 hover:bg-brand-600"
      >
        Open Agami
      </a>
    </div>
  );
}
